<template>
    <div class="valueSlider" :class="{ hasTooltips: tooltip }">
        <div ref="valueSlider" :disabled="disabled"></div>
    </div>
</template>

<script>
import noUiSlider from 'nouislider';
import 'nouislider/distribute/nouislider.css';

export default {
    name: 'ValueSlider',

    model: {
        prop: 'outerModel'
    },

    props: {
        outerModel: {
            // for v-model
            type: [String, Number, Array],
            required: true,
            validator(value) {
                if (Array.isArray(value)) {
                    return value.length === 2 && !isNaN(+value[0]) && !isNaN(+value[1]);
                }
                return !isNaN(+value);
            }
        },
        min: {
            // lower range limit
            type: [Number, String],
            required: true
        },
        max: {
            // upper range limit
            type: [Number, String],
            required: true
        },
        range: {
            // override min, max and step to directly control noUiSlider range; see https://refreshless.com/nouislider/slider-values/#section-non-linear
            type: Object,
            default: undefined
        },
        connect: {
            // show bar between handles or the edges of the slider
            type: Boolean,
            default: false
        },
        scale: {
            // show a scale under the slider
            type: Boolean,
            default: false
        },
        step: {
            // force handles to move the specified amount
            type: [Number, String],
            default: undefined
        },
        tooltip: {
            // show tooltip above the handles
            type: Boolean,
            default: false
        },
        formatTooltip: {
            // formatting function for tooltip values
            type: Function,
            default: v => Math.round(v)
        },
        formatScale: {
            type: Function,
            default: null
        },
        disabled: {
            // disable the slider
            type: Boolean,
            default: false
        },
        snap: {
            // jump between steps when a non-linear scale/range has been configured
            type: Boolean,
            default: false
        }
    },

    computed: {
        isRangeSlider() {
            return Array.isArray(this.outerModel);
        },

        currentRange() {
            if (this.range) {
                return this.range;
            }
            return {
                min: +this.min,
                max: +this.max
            };
        }
    },

    watch: {
        currentRange: {
            deep: true,
            handler() {
                if (this.$refs.valueSlider.noUiSlider) {
                    this.$refs.valueSlider.noUiSlider.updateOptions({ range: this.currentRange }, true);
                }
            }
        },

        outerModel: {
            handler(value) {
                const sliderValue = this.$refs.valueSlider.noUiSlider.get();

                if (Array.isArray(value)) {
                    const modelValues = value.map(e => e.toFixed(2));
                    if (modelValues.length !== sliderValue.length || modelValues.some((v, i) => v !== sliderValue[i])) {
                        this.$refs.valueSlider.noUiSlider.set(value);
                    }
                } else if (typeof value === 'number' && value.toFixed(2) !== sliderValue) {
                    this.$refs.valueSlider.noUiSlider.set(value);
                }
            }
        }
    },

    mounted() {
        const options = {
            start: this.outerModel,
            connect: this.isRangeSlider ? [false, this.connect, false] : [this.connect, false],
            step: this.step === undefined ? undefined : +this.step,
            behaviour: 'tap-drag',
            tooltips: this.getTooltipFormatting(),
            range: this.currentRange,
            snap: this.snap,
            pips: !this.scale
                ? undefined
                : {
                      mode: 'positions',
                      values: [0, 25, 50, 75, 100],
                      density: 3,
                      format: this.getScaleFormatting()
                  }
        };

        noUiSlider.create(this.$refs.valueSlider, options);

        this.$refs.valueSlider.noUiSlider.on('set', (values, handle, unencoded) => {
            this.$emit('input', this.isRangeSlider ? unencoded : unencoded[0]);
        });
    },

    methods: {
        getTooltipFormatting() {
            if (!this.tooltip) {
                return undefined;
            }
            const formatting = { to: v => `<span>${this.formatTooltip(v)}</span>` };
            return this.isRangeSlider ? [formatting, formatting] : formatting;
        },

        getScaleFormatting() {
            if (!this.tooltip) {
                return undefined;
            }
            if (this.formatScale !== null) {
                return { to: v => this.formatScale(v) };
            }
            return { to: v => this.formatTooltip(v) };
        }
    }
};
</script>

<style lang="scss">
@import '../assets/styles/settings';

.valueSlider {
    min-height: 70px;
    padding: 5px 17px;

    .noUi-connect {
        background: $color_ci_blue_shade1;
    }
    [disabled] .noUi-connect {
        background: $color_gray_very_light;
    }

    .noUi-value.noUi-value-horizontal.noUi-value-large {
        margin-top: 6px;
    }

    .noUi-horizontal {
        height: 14px;

        .noUi-handle {
            height: 21px;
            top: -4px;
            cursor: ew-resize;

            &:after,
            &:before {
                height: 11px;
                top: 4px;
            }
        }
    }

    .noUi-handle {
        outline: none;
    }

    .noUi-pips.noUi-pips-horizontal {
        font-size: 11px;
        margin-top: -1px;
    }

    $tooltip_height: 17px;

    .noUi-tooltip {
        position: absolute;
        background: transparent;
        padding: 0;
        border: 0;
        font-weight: bold;
        font-size: 80%;
        white-space: nowrap;
        top: -$tooltip_height !important;
        bottom: 0 !important;
    }

    &.hasTooltips {
        padding-top: $tooltip_height;
    }
}
</style>
